import { Fragment, useMemo, useState } from 'react';
import { api, ApiError, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { comboLabel, useRefData, type Combination } from '../../lib/refdata';
import type { Commitment, Invoice, Vendor, Voucher } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function Vouchers() {
  const { persona } = useAuth();
  const { fy, voteByCode } = useRefData();
  const vote = voteByCode(persona?.voteCode);
  const [showCreate, setShowCreate] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const listQ = useApiQuery<{ items: Voucher[] }>(vote ? `/api/v1/expenditure/vouchers?voteId=${vote.id}&limit=100` : null);
  const vendorsQ = useApiQuery<{ items: Vendor[] }>('/api/v1/commitments/vendors?limit=100');
  const vendorName = useMemo(() => {
    const m = new Map((vendorsQ.data?.items ?? []).map((v) => [v.id, v.name]));
    return (id: string) => m.get(id) ?? id;
  }, [vendorsQ.data]);

  const isCert = persona?.roles.includes('MDA_CERTIFYING_OFFICER') ?? false;
  const isAcct = persona?.roles.includes('MDA_ACCOUNTING_OFFICER') ?? false;

  const act = async (voucher: Voucher, action: 'certify' | 'authorise' | 'submit-treasury') => {
    setActionError(null);
    try {
      await api.post(`/api/v1/expenditure/vouchers/${voucher.id}/${action}`);
      listQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : `${action} failed`);
    }
  };

  if (!vote) return <ErrorNote message="No vote bound to this persona." />;

  return (
    <div>
      <PageHeader
        title="Payment vouchers"
        subtitle="Certify → authorise (four-eyes) → submit to Treasury for payment through the Central Bank"
        actions={
          isCert ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + New voucher from matched invoices
            </button>
          ) : undefined
        }
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No payment vouchers" hint="Match an invoice first, then build a voucher from it." />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Voucher</th>
                <th className="table-head text-right">Amount</th>
                <th className="table-head">Invoices</th>
                <th className="table-head">Status</th>
                <th className="table-head">Approvals</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((v) => (
                <Fragment key={v.id}>
                  <tr className="cursor-pointer hover:bg-slate-50" onClick={() => setExpanded(expanded === v.id ? null : v.id)}>
                    <td className="table-cell font-medium">{v.number}</td>
                    <td className="table-cell text-right tabular-nums">{scr(v.amount)}</td>
                    <td className="table-cell text-xs text-slate-500">
                      {(v.invoices ?? []).map((i) => `${i.number} (${vendorName(i.vendorId)})`).join(', ') || '—'}
                    </td>
                    <td className="table-cell">
                      <StatusBadge status={v.status} />
                      {v.status === 'RETURNED' && v.returnReason ? (
                        <div className="mt-1 max-w-xs text-[11px] text-rose-600">{v.returnReason}</div>
                      ) : null}
                    </td>
                    <td className="table-cell text-[11px] text-slate-400">
                      {(v.mdaApprovals ?? []).map((a) => `${a.role.replaceAll('_', ' ')} — ${a.by}`).join(' · ') || '—'}
                    </td>
                    <td className="table-cell text-right" onClick={(e) => e.stopPropagation()}>
                      {isCert && v.status === 'DRAFT' ? (
                        <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => act(v, 'certify')}>
                          Certify
                        </button>
                      ) : null}
                      {isAcct && v.status === 'MDA_CERTIFIED' ? (
                        <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => act(v, 'authorise')}>
                          Authorise
                        </button>
                      ) : null}
                      {isAcct && (v.status === 'MDA_AUTHORISED' || v.status === 'RETURNED') ? (
                        <button className="btn-primary !px-2.5 !py-1 text-xs" onClick={() => act(v, 'submit-treasury')}>
                          Submit to Treasury
                        </button>
                      ) : null}
                    </td>
                  </tr>
                  {expanded === v.id ? (
                    <tr>
                      <td colSpan={6} className="border-b border-slate-100 bg-slate-50 px-6 py-4">
                        <VoucherDetail voucher={v} vendorName={vendorName} />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && fy ? (
        <CreateVoucherModal
          voteId={vote.id}
          fyId={fy.id}
          vendors={vendorsQ.data?.items ?? []}
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function VoucherDetail({ voucher, vendorName }: { voucher: Voucher; vendorName: (id: string) => string }) {
  return (
    <div className="grid gap-4 text-sm sm:grid-cols-2">
      <div>
        <div className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">Invoices</div>
        {(voucher.invoices ?? []).map((i) => (
          <div key={i.id} className="mb-1 flex justify-between rounded bg-white px-3 py-1.5 ring-1 ring-slate-200">
            <span>
              {i.number} · {vendorName(i.vendorId)}
            </span>
            <span className="tabular-nums">{scr(i.amount)}</span>
          </div>
        ))}
        {(voucher.retentions ?? []).length > 0 ? (
          <>
            <div className="mb-1 mt-3 text-xs font-bold uppercase tracking-wide text-slate-500">Retentions</div>
            {(voucher.retentions ?? []).map((r) => (
              <div key={r.id} className="mb-1 flex justify-between rounded bg-white px-3 py-1.5 ring-1 ring-slate-200">
                <span>
                  {r.percent}% held — {r.releaseCondition}
                </span>
                <span>
                  {scr(r.amount)} <StatusBadge status={r.status} />
                </span>
              </div>
            ))}
          </>
        ) : null}
      </div>
      <div>
        <div className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">Approval trail</div>
        {(voucher.mdaApprovals ?? []).map((a, idx) => (
          <div key={idx} className="mb-1 rounded bg-white px-3 py-1.5 ring-1 ring-slate-200">
            <span className="font-medium">{a.role.replaceAll('_', ' ')}</span> — {a.by}
            <span className="ml-2 text-xs text-slate-400">{formatDateTime(a.at)}</span>
          </div>
        ))}
        {voucher.scheduledAt ? (
          <div className="mt-2 text-xs text-slate-500">Scheduled for payment {formatDateTime(voucher.scheduledAt)}</div>
        ) : null}
      </div>
    </div>
  );
}

function CreateVoucherModal({
  voteId,
  fyId,
  vendors,
  onClose,
  onDone,
}: {
  voteId: string;
  fyId: string;
  vendors: Vendor[];
  onClose: () => void;
  onDone: () => void;
}) {
  const invoicesQ = useApiQuery<{ items: Invoice[] }>('/api/v1/expenditure/invoices?status=MATCHED&limit=100');
  const commitmentsQ = useApiQuery<{ items: Commitment[] }>(`/api/v1/commitments/?voteId=${voteId}&limit=100`);
  const combosQ = useApiQuery<{ items: Combination[] }>(`/api/v1/coa/combinations?voteId=${voteId}&active=true&limit=100`);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [splits, setSplits] = useState<Array<{ coaCombinationId: string; amount: string }>>([]);
  const [retain, setRetain] = useState(false);
  const [retention, setRetention] = useState({ percent: '10', releaseCondition: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const vendorName = (id: string) => vendors.find((v) => v.id === id)?.name ?? id;
  const commitmentById = useMemo(
    () => new Map((commitmentsQ.data?.items ?? []).map((c) => [c.id, c])),
    [commitmentsQ.data],
  );
  // Only invoices whose commitment belongs to this vote can go on this voucher.
  const eligible = (invoicesQ.data?.items ?? []).filter(
    (i) => !i.commitmentId || commitmentById.has(i.commitmentId),
  );

  const total = eligible.filter((i) => selected.has(i.id)).reduce((s, i) => s + Number.parseFloat(i.amount), 0);
  const splitSum = splits.reduce((s, r) => s + (Number.parseFloat(r.amount) || 0), 0);

  const toggle = (inv: Invoice) => {
    const next = new Set(selected);
    if (next.has(inv.id)) next.delete(inv.id);
    else next.add(inv.id);
    setSelected(next);
    // Re-derive default splits from the selected invoices' commitments.
    const chosen = eligible.filter((i) => next.has(i.id));
    const byCombo = new Map<string, number>();
    for (const i of chosen) {
      const combo = i.commitmentId ? commitmentById.get(i.commitmentId)?.coaCombinationId : undefined;
      if (combo) byCombo.set(combo, (byCombo.get(combo) ?? 0) + Number.parseFloat(i.amount));
    }
    setSplits([...byCombo.entries()].map(([coaCombinationId, amount]) => ({ coaCombinationId, amount: String(amount) })));
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/expenditure/vouchers', {
        voteId,
        fiscalYearId: fyId,
        invoiceIds: [...selected],
        coaSplits: splits.map((s) => ({ coaCombinationId: s.coaCombinationId, amount: Number.parseFloat(s.amount) })),
        ...(retain && retention.releaseCondition
          ? { retention: { percent: Number.parseFloat(retention.percent), releaseCondition: retention.releaseCondition } }
          : {}),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create voucher');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New payment voucher" onClose={onClose} wide>
      <div className="space-y-4">
        {error ? <ErrorNote message={error} /> : null}

        <div>
          <div className="field-label">Matched invoices</div>
          {invoicesQ.loading ? (
            <Spinner />
          ) : eligible.length === 0 ? (
            <EmptyState title="No matched invoices" hint="Capture and match an invoice first." />
          ) : (
            <div className="max-h-52 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
              {eligible.map((i) => (
                <label key={i.id} className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-slate-50">
                  <input type="checkbox" checked={selected.has(i.id)} onChange={() => toggle(i)} />
                  <span className="flex-1 text-sm">
                    <span className="font-medium">{i.number}</span> · {vendorName(i.vendorId)}
                  </span>
                  <span className="text-sm tabular-nums">{scr(i.amount)}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="field-label">Charge to budget lines (must equal invoice total)</div>
          {splits.map((row, idx) => (
            <div key={idx} className="mb-2 flex gap-2">
              <select
                className="field-input flex-1"
                value={row.coaCombinationId}
                onChange={(e) => setSplits(splits.map((r, i) => (i === idx ? { ...r, coaCombinationId: e.target.value } : r)))}
              >
                <option value="">Select budget line…</option>
                {(combosQ.data?.items ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {comboLabel(c)}
                  </option>
                ))}
              </select>
              <input
                className="field-input w-40"
                type="number"
                min="0"
                step="0.01"
                value={row.amount}
                onChange={(e) => setSplits(splits.map((r, i) => (i === idx ? { ...r, amount: e.target.value } : r)))}
              />
              <button className="btn-secondary !px-2.5" onClick={() => setSplits(splits.filter((_, i) => i !== idx))}>
                ✕
              </button>
            </div>
          ))}
          <button className="btn-secondary !py-1.5 text-xs" onClick={() => setSplits([...splits, { coaCombinationId: '', amount: '' }])}>
            + Add split
          </button>
          <div className={`mt-2 text-sm ${Math.abs(splitSum - total) < 0.005 ? 'text-emerald-600' : 'text-rose-600'}`}>
            Splits {scr(splitSum)} / invoices {scr(total)}
          </div>
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" checked={retain} onChange={(e) => setRetain(e.target.checked)} />
            Hold a retention
          </label>
          {retain ? (
            <div className="mt-2 grid grid-cols-3 gap-3">
              <Field label="Percent (max 20)">
                <input
                  className="field-input"
                  type="number"
                  min="0.1"
                  max="20"
                  value={retention.percent}
                  onChange={(e) => setRetention({ ...retention, percent: e.target.value })}
                />
              </Field>
              <Field label="Release condition" className="col-span-2">
                <input
                  className="field-input"
                  value={retention.releaseCondition}
                  onChange={(e) => setRetention({ ...retention, releaseCondition: e.target.value })}
                  placeholder="e.g. Defects liability period ends 31 Dec 2026"
                />
              </Field>
            </div>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={
              busy ||
              selected.size === 0 ||
              splits.length === 0 ||
              splits.some((s) => !s.coaCombinationId || !s.amount) ||
              Math.abs(splitSum - total) >= 0.005
            }
          >
            {busy ? 'Creating…' : 'Create voucher'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
